import { useEffect, useRef, useState } from "react";
import { renameConversationBranch } from "../api/conversations";
import { HttpError } from "../api/errors";
import { friendlyError } from "../api/errorMessages";
import { useChatStream } from "../hooks/useChatStream";
import { isValidBranchName } from "../lib/branchName";
import Composer from "./Composer";
import DeployButton from "./DeployButton";
import ForbiddenView from "./ForbiddenView";
import IssuesPanel from "./IssuesPanel";
import Message from "./Message";

type Tab = "chat" | "issues";

const TABS: readonly Tab[] = ["chat", "issues"];
const TAB_STORAGE_KEY = "taiw.chat-tab";

// Persist the active tab across reloads. Reads are validated against the allowed
// set so a stale/garbage value falls back to "chat".
function readTab(): Tab {
  try {
    const v = localStorage.getItem(TAB_STORAGE_KEY);
    if (v && (TABS as readonly string[]).includes(v)) return v as Tab;
  } catch { /* ignore */ }
  return "chat";
}

/**
 * The rename answers with five different bodies (src/server.ts, PATCH
 * /conversations/:project/:id): three coded 409s (`branch_exists`, `in_flight`,
 * `branch_shared`), a coded 400 carrying git's raw stderr (`rename_failed`),
 * and a bare-prose 400 with no code at all ("conversation has no branch to
 * rename"). All five have to reach the user as a sentence, so an UNCODED
 * failure falls back to the daemon's own message below.
 *
 * That fallback is now belt and braces rather than the only guard: friendlyError
 * used to flatten an uncoded failure to "a request failed (HTTP 400)" and throw
 * away the only text saying what went wrong, but its `unknown` branch now
 * prefers `rawMessage` like its six siblings. The local fallback is kept because
 * it is NOT identical: it returns the message untrimmed where friendlyError
 * trims it, so removing it changes behaviour on this path and wants its own
 * test rather than a drive-by deletion.
 *
 * Whatever comes back is rendered as a text child, never as markup — git's
 * output is arbitrary (multi-line, quotes, angle brackets).
 */
function branchErrorText(e: unknown): string {
  if (e instanceof HttpError) {
    const info = friendlyError({ status: e.status, code: e.code, rawMessage: e.message });
    if (info.code !== "unknown") return info.hint ? `${info.body} ${info.hint}` : info.body;
    return e.message || info.body;
  }
  return (e as Error | undefined)?.message || "Could not rename the branch.";
}

type Props = {
  project: string;
  /**
   * Display name for `project`. The registry id is the database key and can
   * differ from the product name (`multipack` is the Ultimate Survival
   * Add-On), so the header shows this and falls back to the id only when the
   * registry has not been loaded.
   */
  projectName?: string;
  conversationId: number;
  branch: string | null;
  /**
   * Absolute path of the worktree the daemon pinned for this conversation.
   * Null/absent until a branch rename pins it. The derivation lives
   * server-side, so an absent value renders as NOTHING — a guessed path would
   * name a directory that does not exist.
   */
  worktreeDir?: string | null;
  focused: boolean;
  onClose: () => void;
  onFocus: () => void;
  /** Open a copied conversation as a focused new column (Issues takeover). */
  onOpenConversation?: (conversationId: number, branch: string | null, worktreeDir?: string | null) => void;
};

export default function ChatColumn({
  project,
  projectName,
  conversationId,
  branch,
  worktreeDir,
  focused,
  onClose,
  onFocus,
  onOpenConversation,
}: Props) {
  const displayName = projectName ?? project;
  const {
    messages,
    send,
    stop,
    isStreaming,
    isLoadingHistory,
    error,
    errorInfo,
    clearError,
    continueTurn,
    isResuming,
    queue,
    removeQueued,
    updateQueued,
    moveQueued,
    queuePaused,
    queuePausedReason,
    resumeQueue,
    retryQueued,
  } = useChatStream(project, conversationId);

  // Stop is available while a turn streams AND during the auto-resume backoff
  // window (a resume is scheduled but not yet streaming).
  const canStop = isStreaming || isResuming;

  const [tab, setTab] = useState<Tab>(readTab);

  // A branch rename performed in THIS column. Null until one happens, so the
  // props stay the source of truth; afterwards it wins, because the parent's
  // ColumnRef still holds the name the column was opened with.
  const [renamed, setRenamed] = useState<{ branch: string; worktreeDir?: string } | null>(null);
  // The rename form: null means closed, a string is the editable draft. Kept
  // separate from `renamed` so an in-progress edit never repaints the header.
  const [branchDraft, setBranchDraft] = useState<string | null>(null);
  const [branchError, setBranchError] = useState<string | null>(null);
  const [branchNotice, setBranchNotice] = useState<string | null>(null);
  const [savingBranch, setSavingBranch] = useState(false);

  const shownBranch = renamed?.branch ?? branch;
  // A rename response without `worktreeDir` (the daemon's same-name early
  // return) means "unchanged", never "cleared" — so it falls back to the last
  // path we knew, and only then to nothing.
  const shownWorktree = renamed?.worktreeDir ?? worktreeDir ?? null;

  const openBranchRename = () => {
    if (!shownBranch) return;
    setBranchDraft(shownBranch);
    setBranchError(null);
    setBranchNotice(null);
  };

  const closeBranchRename = () => {
    setBranchDraft(null);
    setBranchError(null);
  };

  const submitBranchRename = async () => {
    if (savingBranch || branchDraft === null) return;
    const from = shownBranch;
    if (!from) return;
    const next = branchDraft.trim();
    if (next === "") {
      setBranchError("Branch name must not be empty.");
      return;
    }
    // Catch a typo before the round trip. The daemon runs the real
    // `git check-ref-format` and stays the authority — this is a courtesy.
    if (!isValidBranchName(next)) {
      setBranchError(`Invalid branch name: ${next}`);
      return;
    }
    setBranchError(null);
    setSavingBranch(true);
    try {
      const out = await renameConversationBranch(project, conversationId, next);
      // Keep the previous path when the response omits one, or renaming to the
      // same name would blank a directory the daemon never touched.
      setRenamed((prev) => ({ branch: out.branch, worktreeDir: out.worktreeDir ?? prev?.worktreeDir }));
      setBranchDraft(null);
      if (out.branch !== from) {
        // Nothing deletes the old branch from origin: git renames locally and
        // the daemon does not push. Say so, naming it, or the user is left with
        // a stale remote branch they never hear about.
        setBranchNotice(`Renamed. If ${from} was already pushed, it still exists on origin and is not deleted.`);
        // The sidebar caches each row's branch and hands it back when the
        // column is reopened; this is the drawer's own refresh signal.
        window.dispatchEvent(
          new CustomEvent("thunderay-ai:conversation-activity", { detail: { project } }),
        );
      }
    } catch (e) {
      // Leave the form open with what was typed so a refused name (409
      // branch_exists) can be corrected in place. A 409 branch_shared is not
      // about the name at all — the form stays open there too, but the way out
      // is archiving the other conversations, which the error's hint says.
      setBranchError(branchErrorText(e));
    } finally {
      setSavingBranch(false);
    }
  };

  // A column can be pointed at a different conversation in place — useChatStream
  // reloads on exactly this prop change — so a rename recorded for the old one
  // must not linger over the new one's branch, nor its open form or notice.
  useEffect(() => {
    setRenamed(null);
    setBranchDraft(null);
    setBranchError(null);
    setBranchNotice(null);
  }, [project, conversationId]);

  // Restore the active tab on reload by persisting it to localStorage.
  useEffect(() => {
    try { localStorage.setItem(TAB_STORAGE_KEY, tab); } catch { /* ignore */ }
  }, [tab]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Esc to stop — only responds when this column is focused.
  useEffect(() => {
    if (!focused) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && canStop) {
        e.preventDefault();
        void stop();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canStop, stop, focused]);

  const isForbidden = error instanceof HttpError && error.status === 403;

  if (isForbidden) {
    return (
      <div
        className={`chat-panel chat-column${focused ? " focused" : ""}`}
        onMouseDown={onFocus}
      >
        <div className="chat-header">
          <div className="chat-header-id">
            <span className="chat-header-title">
              <span className="chat-header-name">{displayName}</span>
            </span>
            {branch && (
              <span className="column-branch" title={branch}>
                {branch}
              </span>
            )}
            <button type="button" aria-label="Close column" className="column-close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>
        <ForbiddenView projectId={project} projectName={displayName} />
      </div>
    );
  }

  return (
    <div
      className={`chat-panel chat-column${focused ? " focused" : ""}`}
      onMouseDown={onFocus}
    >
      <div className="chat-header">
        <div className="chat-header-id">
          <span className="chat-header-title">
            <span className="glyph glyph-addon" aria-hidden="true">
              {displayName.slice(0, 1).toUpperCase()}
            </span>
            <span className="chat-header-name">{displayName}</span>
          </span>
          {shownBranch && (
            <button
              type="button"
              className="column-branch"
              title={`Branch ${shownBranch} — click to rename`}
              onClick={openBranchRename}
            >
              {shownBranch}
            </button>
          )}
          {shownWorktree && (
            // Read-only, and deliberately not a control: one click selects the
            // whole path (CSS user-select: all) so it can be copied.
            <span className="column-worktree" title={shownWorktree}>
              {shownWorktree}
            </span>
          )}
          <button type="button" aria-label="Close column" className="column-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="chat-header-actions">
          <div className="chat-tabs">
            <button
              type="button"
              className={`chat-tab${tab === "chat" ? " active" : ""}`}
              onClick={() => setTab("chat")}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
              </svg>
              Chat
            </button>
            <button
              type="button"
              className={`chat-tab${tab === "issues" ? " active" : ""}`}
              onClick={() => setTab("issues")}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              Issues
            </button>
            <button
              type="button"
              className="chat-tab"
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent("thunderay-ai:open-qa", { detail: { project } }),
                )
              }
              title="Bugs Emil found in the latest build, with screenshots"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M8 3h8M9 3v3.5a4 4 0 0 1-1.2 2.8L5 12l2.8 2.7A4 4 0 0 1 9 17.5V21h6v-3.5a4 4 0 0 1 1.2-2.8L19 12l-2.8-2.7A4 4 0 0 1 15 6.5V3" />
              </svg>
              Emil
            </button>
            <button
              type="button"
              className="chat-tab"
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent("thunderay-ai:open-review", { detail: { project } }),
                )
              }
              title="Automatic PR code review queue"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m9 9-3 3 3 3M15 9l3 3-3 3M13 6l-2 12" />
              </svg>
              Code Review
            </button>
          </div>
          {tab === "chat" && isStreaming && (
            <span className="live" aria-live="polite">
              <span className="pulse" aria-hidden="true" /> streaming
            </span>
          )}
          {tab === "chat" && isResuming && !isStreaming && (
            <span className="live" aria-live="polite">
              <span className="pulse" aria-hidden="true" /> resuming
            </span>
          )}
          {tab === "chat" && canStop && (
            <button
              type="button"
              className="stop-btn"
              onClick={() => void stop()}
              title="Stop the bot's current response"
            >
              <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
              Stop
            </button>
          )}
          {tab === "chat" && <DeployButton project={project} />}
        </div>
        {/* Rename + its outcome sit on their own header rows: full-width in a
            wrapping flex row, so neither squeezes the identity or the tabs. */}
        {branchDraft !== null && (
          <form
            className="column-branch-form"
            onSubmit={(e) => { e.preventDefault(); void submitBranchRename(); }}
            onKeyDown={(e) => {
              if (e.key !== "Escape") return;
              e.preventDefault();
              // Escape here means "cancel the edit" and nothing else: without
              // this the same keypress reaches the window listener above and
              // stops a streaming turn.
              e.stopPropagation();
              closeBranchRename();
            }}
          >
            <label className="column-branch-field">
              <span>Branch</span>
              <input
                type="text"
                className="column-branch-input"
                aria-label="Branch name"
                value={branchDraft}
                onChange={(e) => setBranchDraft(e.target.value)}
                placeholder="ai/my-feature"
                spellCheck={false}
                autoComplete="off"
                autoFocus
              />
            </label>
            <div className="column-branch-actions">
              <button type="submit" className="column-branch-save" disabled={savingBranch}>
                {savingBranch ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className="column-branch-cancel"
                onClick={closeBranchRename}
                disabled={savingBranch}
              >
                Cancel
              </button>
            </div>
            {/* A text child, never markup: `rename_failed` carries git's raw
                stderr, which is neither short nor well-formed. */}
            {branchError && (
              <span className="column-branch-error" role="alert">{branchError}</span>
            )}
          </form>
        )}
        {branchNotice && (
          <div className="column-branch-notice" role="status">
            <span>{branchNotice}</span>
            <button
              type="button"
              className="column-branch-notice-dismiss"
              aria-label="Dismiss branch notice"
              onClick={() => setBranchNotice(null)}
            >
              ×
            </button>
          </div>
        )}
      </div>

      {tab === "issues" ? (
        <IssuesPanel project={project} onOpenConversation={onOpenConversation} />
      ) : (
        <>
          {(errorInfo || error) && (
            <div className="error-banner" role="alert">
              <span>{errorInfo ? `${errorInfo.title}: ${errorInfo.body}` : error?.message}</span>
              <button type="button" onClick={clearError}>
                Dismiss
              </button>
            </div>
          )}

          <div className="messages" ref={scrollRef}>
            {isLoadingHistory && messages.length === 0 && (
              <p className="placeholder">Loading history…</p>
            )}
            {!isLoadingHistory && messages.length === 0 && (
              <p className="placeholder">No messages yet. Say hi to {displayName}.</p>
            )}
            {messages.map((m) => (
              <Message key={m.id} message={m} onContinue={continueTurn} />
            ))}
          </div>

          <Composer
            disabled={isLoadingHistory}
            streaming={isStreaming}
            onSend={send}
            queue={queue}
            onRemoveQueued={removeQueued}
            paused={queuePaused}
            pausedReason={queuePausedReason}
            onResumeQueue={resumeQueue}
            onRetryQueued={retryQueued}
            onUpdateQueued={updateQueued}
            onMoveQueued={moveQueued}
          />
        </>
      )}
    </div>
  );
}
